import { cva, type VariantProps } from "class-variance-authority";
import { forwardRef, type ButtonHTMLAttributes } from "react";
import { cn } from "../lib/cn";

/**
 * The design gives each variant its OWN size — primary is 7px/14px at 12px
 * type, ghost is 5px/10px at 13px. That is not an oversight to normalise away:
 * a ghost button sits inline with body text and a primary sits alone, so they
 * are tuned to different jobs. Exposing a separate `size` prop would invite
 * combinations the design never drew, so variant carries size and there is no
 * size prop.
 */
const button = cva(
  [
    "inline-flex items-center justify-center gap-1.5",
    // Explicit, not inherited: Chromium's UA sheet gives every <button> its
    // own font (400 13.333px Arial). `font-sans` is mapped to the product's
    // stack in tokens.css, so this is the product's face, not Tailwind's.
    "rounded-control font-sans cursor-pointer",
    // An icon is as tall as the label, because in the design it IS a
    // character: the header action's leading "+" is a typed glyph, so its box
    // is the font size by definition. `1em` reproduces that per variant —
    // 13px on `ghost`, 12px on the rest — instead of every call site passing
    // a size and the header button coming out a pixel wide.
    "[&>svg]:size-[1em]",
    "transition-colors",
    // The design draws no focus state. A keyboard user still needs one, so the
    // ring is added here rather than left to each app to reinvent — it uses
    // the same token the inputs focus with, so the two read as one system.
    "outline-none focus-visible:ring-2 focus-visible:ring-info/60",
    "disabled:cursor-not-allowed disabled:opacity-50",
  ],
  {
    variants: {
      /**
       * Each variant pins its own line box, so height is a property of the
       * variant rather than of whatever the label happens to contain.
       *
       * The design's own heights come out of glyph metrics: its header action
       * is 28px only because the fullwidth `＋` in the label forces an 18px
       * line box, while its 12px-text buttons get 14px. Reproducing that by
       * accident means a button silently changes height when someone edits its
       * copy. Pinning `leading` reproduces the same pixels on purpose —
       * primary 28, secondary 28, ghost 28, danger 26.
       */
      variant: {
        primary:
          "bg-brand text-on-brand font-semibold text-secondary leading-[14px] px-3.5 py-[7px] border-none hover:bg-brand-hover",
        secondary:
          "bg-transparent text-text3 text-secondary leading-[14px] px-3 py-1.5 border border-border2 hover:bg-raised2",
        // Height pinned directly, line box left `normal` — the design's own
        // arrangement. Pinning `leading-[18px]` instead reproduced the 28px
        // height but sat the label half a pixel high inside it, which is a
        // different rasterisation of every glyph.
        ghost:
          "bg-transparent text-text3 text-body leading-[normal] h-7 px-2.5 py-[5px] border-none hover:bg-ghosthover",
        danger:
          "bg-transparent text-danger text-secondary leading-[14px] px-3 py-[5px] border border-danger-bd2 hover:bg-danger-hover-bg",
      },
      fullWidth: { true: "w-full", false: "" },
    },
    defaultVariants: { variant: "primary", fullWidth: false },
  },
);

/**
 * The button's chrome, for the one element that cannot be a `<button>`: a
 * router link that acts as the page's primary action.
 *
 * Exported rather than left private because the alternative is every app
 * transcribing the class list onto its own `<Link>`, which is how a "button"
 * in one portal ends up a different height from the real ones beside it. A
 * caller picks a variant; it never assembles the classes.
 *
 * Use it ONLY where the element must be an anchor. A control that does
 * something rather than going somewhere is a `<Button>`, and styling a link to
 * look like one does not give it the Space key or a button's role.
 */
export const buttonClass = button;

export type ButtonClassOptions = VariantProps<typeof button>;

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof button>;

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  function Button({ className, variant, fullWidth, type, ...props }, ref) {
    return (
      <button
        ref={ref}
        // An unset `type` inside a form defaults to "submit" and silently
        // submits it. Defaulting to "button" makes the submitting case opt-in.
        type={type ?? "button"}
        className={cn(button({ variant, fullWidth }), className)}
        {...props}
      />
    );
  },
);
